import { AxiosResponse } from "axios";
import { call, fork, put, takeLatest } from "redux-saga/effects";

import {
  AuthResetPasswordRequest,
  AuthResetPasswordResponse,
  AuthSignInRequest,
  AuthSignInResponse,
  AuthUser,
} from "entities/auth";
import { UserRole } from "entities/common";
import * as AuthApi from "services/http/auth";
import { TOKEN_EXPIRY_DAYS, TOKEN_KEY } from "utils/constants";
import { CookieHelper } from "utils/helpers";
import { PAGES } from "utils/links";

import {
  getCurrentUserRequest,
  getCurrentUserSuccess,
  loginRequest,
  loginSuccess,
  logoutRequest,
  logoutSuccess,
  requestResetFailure,
  requestResetRequest,
  requestResetSuccess,
  resetPasswordFailure,
  resetPasswordRequest,
  resetPasswordSuccess,
  setErrors,
} from "../slices/authSlice";

function setTokenCookie(token: string, persistent: boolean) {
  const options: { path: string; expires?: string } = { path: "/" };

  if (persistent) {
    const expires = new Date(Date.now() + TOKEN_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
    options.expires = expires.toUTCString();
  }

  CookieHelper.setCookie(TOKEN_KEY, token, options);
}

function redirectByRole(role: string) {
  if (role === UserRole.ADMINISTRATOR) {
    window.location.href = PAGES.COMPANY_LIST;
  } else {
    window.location.href = PAGES.PORTFOLIOS_LIST;
  }
}

// --- Login ---

function* workerAuthLogin({ payload }: { payload: AuthSignInRequest }): Generator {
  try {
    const response = (yield call(AuthApi.signIn, payload)) as AxiosResponse<AuthSignInResponse>;
    const { access_token, user } = response.data;

    setTokenCookie(access_token, payload.remember_me);
    yield put(loginSuccess(user));
    redirectByRole(user.role);
  } catch (error) {
    const err = error as { detail?: string };
    yield put(setErrors(err?.detail || "Invalid email or password."));
  }
}

function* watchAuthLogin() {
  yield takeLatest(loginRequest.type as unknown as Parameters<typeof takeLatest>[0], workerAuthLogin);
}

// --- Logout ---

function* workerAuthLogout(): Generator {
  CookieHelper.deleteCookie(TOKEN_KEY, { path: "/" });
  yield put(logoutSuccess());
  window.location.href = PAGES.LOGIN;
}

function* watchAuthLogout() {
  yield takeLatest(logoutRequest.type as unknown as Parameters<typeof takeLatest>[0], workerAuthLogout);
}

// --- Get Current User ---

function* workerGetCurrentUser(): Generator {
  try {
    const response = (yield call(AuthApi.getMe)) as AxiosResponse<AuthUser>;
    yield put(getCurrentUserSuccess(response.data));
  } catch {
    CookieHelper.deleteCookie(TOKEN_KEY, { path: "/" });
    yield put(logoutSuccess());
  }
}

function* watchGetCurrentUser() {
  yield takeLatest(getCurrentUserRequest.type as unknown as Parameters<typeof takeLatest>[0], workerGetCurrentUser);
}

// --- Request Password Reset ---

function* workerRequestReset({ payload }: { payload: { email: string } }): Generator {
  try {
    yield call(AuthApi.requestReset, payload);
    yield put(requestResetSuccess());
  } catch (error) {
    const err = error as { detail?: string };
    yield put(requestResetFailure(err?.detail || "Something went wrong. Please try again."));
  }
}

function* watchRequestReset() {
  yield takeLatest(requestResetRequest.type as unknown as Parameters<typeof takeLatest>[0], workerRequestReset);
}

// --- Reset Password (Set New Password) ---

function* workerResetPassword({ payload }: { payload: AuthResetPasswordRequest }): Generator {
  try {
    const response = (yield call(AuthApi.resetPassword, payload)) as AxiosResponse<AuthResetPasswordResponse>;
    const { access_token, user } = response.data;

    setTokenCookie(access_token, true);
    yield put(resetPasswordSuccess(user));
    redirectByRole(user.role);
  } catch (error) {
    const err = error as { detail?: string };
    yield put(resetPasswordFailure(err?.detail || "Reset link is invalid or has expired."));
  }
}

function* watchResetPassword() {
  yield takeLatest(resetPasswordRequest.type as unknown as Parameters<typeof takeLatest>[0], workerResetPassword);
}

export const authWatchers = [
  fork(watchAuthLogin),
  fork(watchAuthLogout),
  fork(watchGetCurrentUser),
  fork(watchRequestReset),
  fork(watchResetPassword),
];
